import React from "react";
import HeroSlider from "@/components/HeroSlider";

/**
 * PageHero — site-wide hero wrapper.
 *
 * Guarantees every public page follows the same hero pattern as the
 * homepage:
 *
 *   N image background slides — page-specific photography. Each slide
 *   is emitted as an <OptimizedImage> with `data-cms-role="slide.<i>.image"`
 *   so /admin/images and the inline Edit Mode can both manage them
 *   independently per page (slot id `cms.<scope>.slide.<i>.image`).
 *
 * The narrative (overline / title / titleAccent / sub / CTAs / badge)
 * is shared across every slide so the messaging stays consistent while
 * the visitor scrubs through the gallery. Pages that need slide-by-slide
 * narratives (HomePage, HotelDetailPage) should continue to use
 * `<HeroSlider>` directly.
 *
 * Historical note: until iteration 28 PageHero injected a brand-wide
 * Mux video as the leading slide on every page. That broke the
 * "every page has its own hero" expectation (all pages showed the
 * same brand poster + video) AND made slide 0 non-editable from
 * /admin/images (because <video> elements have no slot binding).
 * The video has been removed; pages that need a brand video must use
 * <HeroSlider slides={[...]} /> directly with an explicit `video` key.
 *
 * Usage:
 *   <PageHero
 *     images={[
 *       "https://.../atlas-sunset.jpg",
 *       "https://.../erfoud-courtyard.jpg",
 *       "https://.../erg-chebbi-dunes.jpg",
 *     ]}
 *     overline="One family · one calling"
 *     title="Two men · one desert ·"
 *     titleAccent="a story that grew into a collection."
 *     sub="Founded in Erfoud in 1995, Grup Xaluca…"
 *     ctaTo="/booking" ctaLabel="Plan your stay"
 *     secondaryTo="/contact" secondaryLabel="Talk to us"
 *   />
 */

const PageHero = ({
  images = [],
  alt = "Grup Xaluca",
  interval = 8500,
  minHeightClass = "min-h-[100svh]",
  // Optional CMS scope — when set, every slide image emits a stable
  // `data-cms-role` so the inline editor produces deterministic slot
  // ids (cms.<scope>.slide.<i>.image) that mirror cleanly across
  // Preview ↔ Production.
  scope,
  // Narrative — shared across every slide
  badge,
  overline,
  title,
  titleAccent,
  sub,
  ctaTo,
  ctaLabel,
  secondaryTo,
  secondaryLabel,
  // Optional click handler that turns the secondary CTA into a
  // <button> (instead of a Link). Used when the CTA opens an
  // in-page modal — e.g. the "Tu viaje empieza con una conversación"
  // info modal on /booking. Ignored if `secondaryTo` is also set.
  onSecondaryClick,
  // Optional React node rendered inside every slide's narrative,
  // directly beneath the description paragraph. Use this when you need
  // bespoke in-hero controls (e.g. a contact-channel picker) that the
  // built-in two-Link CTA pair cannot express.
  extraCtas,
}) => {
  const cleaned = images.filter(Boolean);
  if (cleaned.length === 0) return null;

  const narrative = { badge, overline, title, titleAccent, sub, ctaTo, ctaLabel, secondaryTo, secondaryLabel, onSecondaryClick, extraCtas };

  // Every slide is a page-specific image. Each one becomes an editable
  // slot (cms.<scope>.slide.<i>.image) so /admin/images can manage them
  // independently per page — no shared brand asset overrides them.
  const slides = cleaned.map((image) => ({ ...narrative, image }));

  return (
    <HeroSlider
      slides={slides}
      alt={alt}
      interval={interval}
      minHeightClass={minHeightClass}
      scope={scope}
    />
  );
};

export default PageHero;
